import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'

import { useCardVerificationsList } from './hooks'
import { CardVerification, CardVerificationStatuses } from './types'
import {
  colorCardVerificationStatus,
  cardVerificationStatus,
} from '../const'
import { path } from '../../../libs'

import { submitReject } from '../atoms/rejectModal/model'
import { approveCardVerification } from '../edit/model'

import {
  Table,
  Tag,
  Button,
  Typography,
  Flex,
  Space,
  Input,
  Divider,
  Empty,
  Alert,
  Avatar,
  Popconfirm,
  Image,
  InputRef,
  DatePicker,
  TimeRangePickerProps,
} from 'antd'
import {
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  CreditCardOutlined,
  FileImageOutlined,
  SafetyOutlined,
  FileTextOutlined,
  CheckOutlined,
  StopOutlined,
  ExperimentOutlined,
} from '@ant-design/icons'
import { FilterValue } from 'antd/es/table/interface'
import { ColumnsType } from 'antd/lib/table'
import Highlighter from 'react-highlight-words'

import { DepositList } from '../atoms/depositsList'
import { CommentList } from '../../../views/widgets/commentList'
import { Guard } from '../../../features/session/guard'

const { Text } = Typography

type DataIndex = keyof CardVerification

const AVATAR_COLORS = ['#3b82f6', '#a855f7', '#06b6d4', '#22c55e', '#f59e0b']
const avatarColor = (n: string) =>
  AVATAR_COLORS[(n || '?').charCodeAt(0) % AVATAR_COLORS.length]

function InfoRow({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <Flex
      justify="space-between"
      align="flex-start"
      style={{ padding: '5px 0', borderBottom: '1px solid var(--crm-border)' }}
    >
      <Text
        type="secondary"
        style={{ fontSize: 16, flexShrink: 0, marginRight: 8 }}
      >
        {label}
      </Text>
      <Text
        style={{
          fontSize: 18,
          textAlign: 'right',
          wordBreak: 'break-all',
        }}
      >
        {value ?? <Text type="secondary">—</Text>}
      </Text>
    </Flex>
  )
}

function DetailSection({
  icon,
  title,
  color,
  badge,
  children,
}: {
  icon: React.ReactNode
  title: string
  color: string
  badge?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div
      style={{
        background: 'var(--crm-bg-elevated)',
        border: '1px solid var(--crm-border)',
        borderRadius: 8,
        padding: '12px 14px',
        marginBottom: 12,
      }}
    >
      <Flex align="center" gap={6} style={{ marginBottom: 10 }}>
        {icon}
        <Text
          style={{
            fontSize: 14,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color,
          }}
        >
          {title}
        </Text>
        {badge}
      </Flex>
      {children}
    </div>
  )
}

function DetailPanel({
  record,
  onRefreshList,
}: {
  record: CardVerification | null
  onRefreshList: () => void
}) {
  const [comment, setComment] = useState('')


  useEffect(() => {
    setComment(record?.comment || '')
  }, [record?.id])

  if (!record) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{ height: '100%' }}
      >
        <Empty description="Select a verification from the list" />
      </Flex>
    )
  }

  const isPending = record.status === 'CLIENT_PURSE_STATUS_PENDING'
  const isDecided =
    record.status === 'CLIENT_PURSE_STATUS_VERIFIED' ||
    record.status === 'CLIENT_PURSE_STATUS_REJECTED'

  const clientId = String(record.clientId)

  const handleApprove = () => {
    ;(approveCardVerification as any)({
      url: `/client_purse/${record.id}`,
      body: {
        requestId: String(record.id),
        status: cardVerificationStatus.cardVerificationRequestStatusAccepted(),
      },
    })
    setTimeout(onRefreshList, 500)
  }

  const handleReject = () => {
    submitReject({
      requestId: String(record.id),
      status: cardVerificationStatus.cardVerificationRequestStatusRejected(),
      comment,
    })
    setTimeout(onRefreshList, 500)
  }

  const statusColor = colorCardVerificationStatus(record.status)
  const statusLabel =
    CardVerificationStatuses[
      record.status as keyof typeof CardVerificationStatuses
    ] || record.status

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '0 16px 24px' }}>
      {/* Header */}
      <Flex
        align="center"
        justify="space-between"
        style={{ padding: '14px 0 10px' }}
      >
        <Flex align="center" gap={10}>
          <Avatar
            size={36}
            style={{
              background: avatarColor(record.client?.fullName || '?'),
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {(record.client?.fullName || '?').slice(0, 2).toUpperCase()}
          </Avatar>
          <div>
            <Flex align="center" gap={6}>
              <Text strong style={{ fontSize: 18 }}>
                Verification {record.id}
              </Text>
              <Tag color={statusColor} style={{ fontSize: 12 }}>
                {statusLabel}
              </Tag>
            </Flex>
            <Text type="secondary" style={{ fontSize: 14 }}>
              Updated {dayjs(record.updatedAt as any).format('DD MMM YYYY, HH:mm')}
            </Text>
          </div>
        </Flex>
      </Flex>

      <Divider style={{ margin: '0 0 14px' }} />

      {/* Client */}
      <DetailSection
        icon={<UserOutlined style={{ fontSize: 12 }} />}
        title="Client"
        color="#3b82f6"
      >
        <InfoRow
          label="User ID"
          value={
            <a
              href={path.clients.details(clientId)}
              target="_blank"
              style={{ fontSize: 16 }}
            >
              {clientId}
            </a>
          }
        />
        <InfoRow label="Name" value={record.client?.fullName} />
        <InfoRow label="Email" value={record.client?.email} />
        <InfoRow
          label="Verified"
          value={
            record.client?.verified ? (
              <Tag
                color="success"
                icon={<CheckCircleOutlined />}
                style={{ fontSize: 10 }}
              >
                Yes
              </Tag>
            ) : (
              <Tag
                color="default"
                icon={<CloseCircleOutlined />}
                style={{ fontSize: 10 }}
              >
                No
              </Tag>
            )
          }
        />
        <InfoRow
          label="Labels"
          value={
            <Flex gap={4}>
              {record.client?.isTest && (
                <Tag color="purple" icon={<ExperimentOutlined />} style={{ fontSize: 10, margin: 0 }}>Test</Tag>
              )}
              {record.client?.isPremium && (
                <Tag color="gold" style={{ fontSize: 10, margin: 0 }}>Premium</Tag>
              )}
              {!record.client?.isTest && !record.client?.isPremium && '—'}
            </Flex>
          }
        />
      </DetailSection>

      {/* Card detail */}
      <DetailSection
        icon={<CreditCardOutlined style={{ fontSize: 12 }} />}
        title="Card detail"
        color="#a855f7"
      >
        <InfoRow label="Mask" value={record.mask} />
        <InfoRow label="Expires" value={record.expiry} />
        <InfoRow label="Type" value={record.cardType} />
        <InfoRow label="Status" value={statusLabel} />
      </DetailSection>

      {/* Documents */}
      <Guard permission="view_uploaded_image_verification">
        <DetailSection
          icon={<FileImageOutlined style={{ fontSize: 12 }} />}
          title="Documents"
          color="#06b6d4"
          badge={
            <Tag style={{ fontSize: 10 }}>
              {record.browserFileNames?.length || 0}
            </Tag>
          }
        >
          {!record.browserFileNames?.length ? (
            <Text type="secondary" style={{ fontSize: 14 }}>
              No documents uploaded
            </Text>
          ) : (
            <Flex gap={8} wrap="wrap">
              {record.browserFileNames.map((link, i) => (
                <div key={link}>
                  <Image width={100} src={link} />
                  <div>
                    <a href={link} target="_blank" style={{ fontSize: 14 }}>
                      doc {i}
                    </a>
                  </div>
                </div>
              ))}
            </Flex>
          )}
        </DetailSection>
      </Guard>

      {/* Reject comment */}
      {record.comment && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 14 }}>Reject comment:</Text>
          <br />
          <Text style={{ fontSize: 16 }}>{record.comment}</Text>
        </div>
      )}

      {/* Decision */}
      {isPending && (
        <div
          style={{
            background: 'var(--crm-decision-bg, #fffbe6)',
            border: '1px solid var(--crm-decision-border, #ffe58f)',
            borderRadius: 8,
            padding: '12px 14px',
            marginBottom: 12,
          }}
        >
          <Flex align="center" gap={6} style={{ marginBottom: 10 }}>
            <FileTextOutlined style={{ color: 'var(--crm-accent-amber)', fontSize: 14 }} />
            <Text
              style={{
                fontSize: 14,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--crm-accent-amber)',
              }}
            >
              Decision
            </Text>
          </Flex>
          <Input.TextArea
            rows={2}
            placeholder="Optional comment…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            style={{ fontSize: 16, marginBottom: 10, resize: 'none' }}
          />
          <Text
            style={{ fontSize: 18, display: 'block', marginBottom: 8, color: 'var(--crm-text)' }}
          >
            Standard rejection:{' '}
            <Text
              code
              style={{ fontSize: 18, cursor: 'pointer' }}
              onClick={() => setComment('info.payment-reject-reason-card')}
            >
              info.payment-reject-reason-card
            </Text>{' '}
            (click to paste)
          </Text>
          <Text
            style={{ fontSize: 16, display: 'block', marginBottom: 10, fontStyle: 'italic', color: 'var(--crm-text)' }}
          >
            "The copy must contain the first 6 digits and the last 4 digits of
            the card number, cardholder's name, expiry date and cardholder's
            signature. Please make a new request and attach the card."
          </Text>
          <Flex gap={8}>
            <Popconfirm
              title="Approve this card verification?"
              okText="Approve"
              cancelText="Cancel"
              onConfirm={handleApprove}
            >
              <Button
                type="primary"
                icon={<CheckOutlined />}
                style={{ flex: 1, background: '#22c55e', borderColor: '#22c55e' }}
              >
                Approve
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Reject this card verification?"
              okText="Reject"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              onConfirm={handleReject}
            >
              <Button danger icon={<StopOutlined />} style={{ flex: 1 }}>
                Reject
              </Button>
            </Popconfirm>
          </Flex>
        </div>
      )}

      {/* Deposits */}
      {clientId !== '0' && (
        <DetailSection
          icon={<SafetyOutlined style={{ fontSize: 12 }} />}
          title="Deposits"
          color="#22c55e"
        >
          <DepositList entityId={record.clientId} />
        </DetailSection>
      )}

      {/* Comments */}
      {clientId !== '0' && (
        <DetailSection
          icon={<UserOutlined style={{ fontSize: 12 }} />}
          title="Comments"
          color="#8b92a3"
        >
          <CommentList entityType="client" entityId={record.clientId} types={[0]} />
        </DetailSection>
      )}

      {/* Already decided */}
      {isDecided && (
        <Alert
          type={record.status === 'CLIENT_PURSE_STATUS_VERIFIED' ? 'success' : 'error'}
          icon={
            record.status === 'CLIENT_PURSE_STATUS_VERIFIED' ? (
              <CheckCircleOutlined />
            ) : (
              <CloseCircleOutlined />
            )
          }
          message={record.status === 'CLIENT_PURSE_STATUS_VERIFIED' ? 'Approved' : 'Rejected'}
          showIcon
        />
      )}
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────

export const CardVerificationsList = () => {
  const [searchParams] = useSearchParams()
  const isStatusInQuery = searchParams.get('status') !== null

  const {
    cardVerifications,
    totalCountOfCardVerifications,
    loading,
    onChangeCardVerificationsList,
  } = useCardVerificationsList()

  const [selectedRecord, setSelectedRecord] = useState<CardVerification | null>(null)

  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [searchText, setSearchText] = useState<{ [key: string]: string }>({})
  const [searchedColumn, setSearchedColumn] = useState('')
  const searchInput = useRef<InputRef>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({
    status: ['CLIENT_PURSE_STATUS_PENDING'],
  })
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    currentSearchText,
    currentSearchedColumn,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    currentSearchText?: string
    currentSearchedColumn?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) setPage(currentPage)
    if (currentPageSize) setPageSize(currentPageSize)
    if (currentSearchText && currentSearchedColumn) {
      setSearchText((prev) => ({
        ...prev,
        [currentSearchedColumn]: currentSearchText,
      }))
    }
    if (currentSearchedColumn) setSearchedColumn(currentSearchedColumn)
    if (currentOrderBy) {
      if (currentOrderBy === 'clientId') currentOrderBy = 'id'
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      setOrderDirection(currentOrderDirection === 'descend' ? '-' : '')
    }
    if (filters) setFiltersState(filters)
  }

  const refreshList = () => {
    onChangeCardVerificationsList({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      searchText,
      searchedColumn,
      filters: {
        status: filtersState?.status && (filtersState.status as string[]),
      },
    })
  }

  useEffect(() => {
    if (isStatusInQuery) {
      setFiltersState({
        ...filtersState,
        status: [searchParams.get('status') || ''],
      })
    }
  }, [searchParams])

  useEffect(() => {
    refreshList()
  }, [page, pageSize, orderBy, orderDirection, searchText, searchedColumn, filtersState])

  const handleSearch = (selectedKeys: string[], dataIndex: DataIndex) => {
    setSearchText((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }))
    setSearchedColumn(dataIndex)
    handleTableChange({
      currentSearchText: selectedKeys[0],
      currentSearchedColumn: dataIndex,
    })
  }

  const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
    clearFilters()
    setSearchText((prev) => {
      delete prev[dataIndex]
      return { ...prev }
    })
  }

  const getColumnSearchProps = (dataIndex: DataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: any) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value: any, record: any) =>
      record[dataIndex]
        ?.toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    filterDropdownProps: {
      onOpenChange: (visible: boolean) => {
        if (visible) setTimeout(() => searchInput.current?.select(), 100)
      },
    },
    render: (text: any) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText[searchedColumn]]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
  ]

  const getColumnSearchDateProps = (dataIndex: DataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      close,
      clearFilters,
      confirm,
    }: any) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <DatePicker.RangePicker
          format="YYYY-MM-DD"
          style={{ marginBottom: 8, display: 'flex' }}
          value={selectedKeys[0]}
          onChange={(e: any) => setSelectedKeys(e ? [e] : [])}
          presets={rangePresets}
          open={isCalendarOpen}
          onClick={() => setIsCalendarOpen(true)}
          allowEmpty={[true, true]}
          onCalendarChange={() => setIsCalendarOpen(false)}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value: any, record: any) => {
      dayjs.extend(isBetween)
      return record[dataIndex]
        ? dayjs(record[dataIndex]).isBetween(
            dayjs(value[0]),
            dayjs(value[1]),
          )
        : false
    },
    filterDropdownProps: {
      onOpenChange: (visible: boolean) => {
        if (visible) setTimeout(() => searchInput.current?.select(), 100)
      },
    },
    render: (text: any) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText[searchedColumn]]}
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const handleRowClick = (record: CardVerification) => {
    setSelectedRecord(record)
  }

  const columns: ColumnsType<CardVerification> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      ...getColumnSearchProps('id'),
      defaultSortOrder: 'descend',
      sorter: (a, b) => parseInt(a.id) - parseInt(b.id),
      render: (v: string) => (
        <Text
          style={{
            fontSize: 18,
            color: '#2563eb',
            fontWeight: 600,
          }}
        >
          {v}
        </Text>
      ),
    },
    {
      title: 'User ID',
      key: 'client',
      width: 100,
      ...getColumnSearchProps('clientId'),
      sorter: (a, b) => parseInt(a.clientId) - parseInt(b.clientId),
      render: (_: any, r: CardVerification) => (
        <Flex align="center" gap={4} wrap="wrap">
          <Text
            style={{
              fontSize: 18,
              color: '#2563eb',
            }}
          >
            {r.clientId}
          </Text>
          {r.client?.isTest && (
            <Tag
              color="purple"
              icon={<ExperimentOutlined />}
              style={{ fontSize: 9, padding: '0 3px', margin: 0 }}
            >
              Test
            </Tag>
          )}
          {r.client?.isPremium && (
            <Tag
              color="gold"
              style={{ fontSize: 9, padding: '0 3px', margin: 0 }}
            >
              Premium
            </Tag>
          )}
        </Flex>
      ),
    },
    {
      title: 'Email',
      key: 'email',
      width: 160,
      ...getColumnSearchProps('client'),
      render: (_: any, r: CardVerification) => (
        <Text style={{ fontSize: 18 }}>{r.client?.email}</Text>
      ),
    },
    {
      title: 'Card mask',
      key: 'mask',
      width: 130,
      render: (_: any, r: CardVerification) => (
        <Text style={{ fontSize: 18 }}>
          {r.mask}
        </Text>
      ),
    },
    {
      title: 'Expiry',
      dataIndex: 'expiry',
      key: 'expiry',
      width: 70,
      render: (v: string) => (
        <Text style={{ fontSize: 18 }}>{v}</Text>
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 100,
      sorter: true,
      ...getColumnSearchDateProps('updatedAt'),
      render: (v: string) => (
        <div>
          <Text style={{ fontSize: 18 }}>
            {dayjs(v).format('DD MMM YY')}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 14 }}>
            {dayjs(v).format('HH:mm')}
          </Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 85,
      filters: [
        { text: 'Unverified', value: 'CLIENT_PURSE_STATUS_UNVERIFIED' },
        { text: 'Invalid', value: 'CLIENT_PURSE_STATUS_INVALID' },
        { text: 'Pending', value: 'CLIENT_PURSE_STATUS_PENDING' },
        { text: 'Rejected', value: 'CLIENT_PURSE_STATUS_REJECTED' },
        { text: 'Verified', value: 'CLIENT_PURSE_STATUS_VERIFIED' },
      ],
      defaultFilteredValue: isStatusInQuery
        ? [searchParams.get('status') || '']
        : ['CLIENT_PURSE_STATUS_PENDING'],
      render: (status: string) => (
        <Tag
          color={colorCardVerificationStatus(status)}
          style={{ fontSize: 10, textTransform: 'capitalize' }}
        >
          {CardVerificationStatuses[
            status as keyof typeof CardVerificationStatuses
          ] || status}
        </Tag>
      ),
    },
  ]

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 500px',
        gap: 12,
        height: 'calc(100vh - 70px)',
        overflow: 'hidden',
      }}
    >
      {/* LEFT: LIST */}
      <div
        style={{
          borderRight: '1px solid var(--crm-border)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Table */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <Table
            columns={columns}
            dataSource={cardVerifications}
            size="small"
            bordered
            scroll={{ x: true }}
            loading={loading}
            rowKey="id"
            pagination={{
              showSizeChanger: true,
              pageSize,
              total: totalCountOfCardVerifications,
              size: 'small',
            }}
            rowClassName={(r) =>
              r.id === selectedRecord?.id ? 'ant-table-row-selected' : ''
            }
            onRow={(r) => ({
              onClick: () => handleRowClick(r),
              style: { cursor: 'pointer' },
            })}
            onChange={(pagination, filters, sorter) =>
              handleTableChange({
                // @ts-ignore
                currentPage: pagination.current,
                // @ts-ignore
                currentPageSize: pagination.pageSize,
                // @ts-ignore
                currentOrderDirection: sorter.order,
                // @ts-ignore
                currentOrderBy: sorter.field,
                filters,
              })
            }
          />
        </div>
      </div>

      {/* RIGHT: DETAIL */}
      <div
        style={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--crm-bg-elevated)',
        }}
      >
        <DetailPanel record={selectedRecord} onRefreshList={refreshList} />
      </div>
    </div>
  )
}
